import { useState } from 'react'
import { ActionIcon, Avatar, Badge, Text } from '@mantine/core'
import { IconCamera } from '@tabler/icons-react'
import { ProfilePhotoModal } from '@/components/ProfilePhotoModal'
import { useBinaryObjectUrl } from '@/hooks/useBinaryObjectUrl'
import { useUpdateMyProfile, type MyProfile } from '@/hooks/useMyProfile'

/**
 * Who the console thinks the member is: their photo (or initials) with the control that
 * adds, replaces or removes it, display name, the console's role label, every Organization
 * they hold an active role in, and the line that says role and Organization are not theirs
 * to edit. The stored photo url is the canonical `Binary/<id>`, shown through an object URL.
 */
export function ProfileSummaryCard({ profile }: { profile: MyProfile }) {
  const photoUrl = useBinaryObjectUrl(profile.photo?.url)
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false)
  const update = useUpdateMyProfile()

  return (
    <section
      aria-label="Summary"
      className="flex flex-col items-center gap-3 rounded-2xl border border-[#E7E9EC] bg-white p-6 text-center shadow-[0_1px_3px_rgba(16,24,40,.05),0_12px_30px_rgba(16,24,40,.05)] md:w-[280px] md:shrink-0"
    >
      <div className="relative">
        <Avatar
          src={photoUrl}
          alt="Photo"
          name={profile.displayName}
          color="#275587"
          size={96}
          radius={9999}
        />
        <ActionIcon
          aria-label={profile.photo ? 'Change photo' : 'Add photo'}
          variant="filled"
          color="#275587"
          radius="xl"
          size="md"
          // Mantine positions the button itself, so the Tailwind `absolute` class loses;
          // the style props win and pin it over the avatar's bottom-right edge.
          pos="absolute"
          right={2}
          bottom={2}
          className="shadow-[0_0_0_2px_#fff]"
          onClick={() => setIsPhotoModalOpen(true)}
        >
          <IconCamera size={16} />
        </ActionIcon>
      </div>
      <div className="flex flex-col items-center gap-1">
        <h1 className="text-xl font-bold tracking-tight text-[#1F2733]">
          {profile.displayName || 'Unnamed member'}
        </h1>
        <div className="flex flex-wrap justify-center gap-1">
          <Badge size="sm" variant="outline">
            {profile.roleLabel}
          </Badge>
          {/* The Active Provider status (see the glossary), set in Provider Operations. */}
          {profile.isClinical && (
            <Badge size="sm" variant="light" color={profile.activeProvider ? 'green' : 'gray'}>
              {profile.activeProvider ? 'Active' : 'Inactive'}
            </Badge>
          )}
        </div>
      </div>
      {profile.organizations.length > 0 && (
        <ul className="m-0 list-none p-0">
          {profile.organizations.map((organization) => (
            <li key={organization}>
              <Text size="sm" c="dimmed">
                {organization}
              </Text>
            </li>
          ))}
        </ul>
      )}
      <Text size="xs" c="dimmed">
        Contact an administrator to change your role or organization
      </Text>

      {isPhotoModalOpen && (
        <ProfilePhotoModal
          userName={profile.displayName}
          hasPhoto={Boolean(profile.photo)}
          photoUrl={photoUrl ?? undefined}
          onClose={() => setIsPhotoModalOpen(false)}
          // Photo only: names and phone are absent, so an unsaved edit on the Personal
          // information tab stays on that form, neither lost nor saved.
          onSave={(photo) => update.mutateAsync({ photo })}
        />
      )}
    </section>
  )
}
